import { OrderGuide } from '@/experiences/sales-portal/domain/order-guides/blocs/types';
import {
  transformOrderGuideItems,
  transformSupplierOrderGuideItems,
} from '@/experiences/sales-portal/domain/order-guides/blocs/transformers/order-guide-items-transformer';
import { useGeneratedGetSalesPortalOrderGuideItemsQuery } from '@/experiences/sales-portal/domain/order-guides/blocs/documents/order-guide-items.generated';
import { useMemo } from 'react';

interface UseOrderGuideItemsLoaderOptions {
  collectionUUID: string;
  /** Distributor-locked guides collapse to their default group (read-only). */
  isDistributorLocked: boolean;
  restaurantUUID: string;
  /** Skips the fetch when the contents are already known — e.g. a just-created empty guide. */
  skip?: boolean;
  supplierUUID: string;
}

interface UseOrderGuideItemsLoaderResult {
  error: ReturnType<
    typeof useGeneratedGetSalesPortalOrderGuideItemsQuery
  >['error'];
  loading: boolean;
  orderGuide?: OrderGuide;
  refetch: ReturnType<
    typeof useGeneratedGetSalesPortalOrderGuideItemsQuery
  >['refetch'];
}

export const useOrderGuideItemsLoader = ({
  collectionUUID,
  isDistributorLocked,
  restaurantUUID,
  skip,
  supplierUUID,
}: UseOrderGuideItemsLoaderOptions): UseOrderGuideItemsLoaderResult => {
  const { data, loading, error, refetch } =
    useGeneratedGetSalesPortalOrderGuideItemsQuery({
      // Autosave writes edits straight to the DB, bypassing the cache, so a
      // cached snapshot is not reliably accurate. So we skip the cache entirely.
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      skip,
      variables: {
        collectionUUID,
        restaurantUUID,
        supplierUUID,
      },
    });

  const orderGuide = useMemo<OrderGuide | undefined>(() => {
    if (!data) {
      return undefined;
    }
    const collection = data.order_guide_restaurant_collection?.[0];
    if (collection) {
      return transformOrderGuideItems(data, collection, isDistributorLocked);
    }
    const supplierCollection = data.order_guide_supplier_collection?.[0];
    if (supplierCollection) {
      return transformSupplierOrderGuideItems(data, supplierCollection);
    }
    return undefined;
  }, [data, isDistributorLocked]);

  return { error, loading, orderGuide, refetch };
};
